"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_native_1 = require("react-native");
const native_1 = require("@react-navigation/native");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const pro_solid_svg_icons_1 = require("@fortawesome/pro-solid-svg-icons");
const pro_light_svg_icons_1 = require("@fortawesome/pro-light-svg-icons");
const store_1 = require("../../store");
const user_1 = require("../../slices/user");
const document_1 = require("../../slices/document");
const DocumentsProvider_1 = require("../../providers/DocumentsProvider");
const useDevice_1 = require("../../hooks/useDevice");
const useAppMessage_1 = require("../../hooks/useAppMessage");
const useDocument_1 = __importDefault(require("../../hooks/useDocument"));
const UploadArea_1 = __importDefault(require("../UploadArea"));
const SourceChip_1 = __importDefault(require("../SourcesModal/SourceChip"));
const ScrollCustomIndicator_1 = __importDefault(require("../ScrollCustomIndicator"));
const SourceButton_1 = __importDefault(require("./SourceButton"));
const container_1 = require("../container");
const GradientButton_1 = require("../GradientButton");
const AppText_1 = require("../AppText");
const LanguageSelectButton_1 = __importDefault(require("../ui-components/LanguageSelectButton"));
const utils_1 = require("../../helper/utils");
const colors_1 = require("../../common/colors");
const styles_1 = require("./styles");
const MAX_SOURCES = 10;
const MIN_CHARACTERS_TEXT_LIMIT = 80;
const MAX_CHARACTERS_TEXT_LIMIT = 448000;
const AddSources = (props) => {
    const { isMobileScreen } = (0, useDevice_1.useDevice)();
    const navigation = (0, native_1.useNavigation)();
    const dispatch = (0, store_1.useAppDispatch)();
    const { generationAbortController } = (0, DocumentsProvider_1.useDocumentsContext)();
    const { showNotification } = (0, useAppMessage_1.useAppMessage)();
    const { attachDocument, generateDocument } = (0, useDocument_1.default)();
    const [sourcesView, setSourcesView] = (0, react_1.useState)('main');
    const [sources, setSources] = (0, react_1.useState)([]);
    const [urlInput, setUrlInput] = (0, react_1.useState)('');
    const [textInput, setTextInput] = (0, react_1.useState)('');
    const [focusOn, setFocusOn] = (0, react_1.useState)('');
    const [selectedLanguage, setSelectedLanguage] = (0, react_1.useState)('English');
    const [isProcessing, setIsProcessing] = (0, react_1.useState)(false);
    const [forceClose, setForceClose] = (0, react_1.useState)(false);
    const sourcesRef = (0, react_1.useRef)(null);
    const isGenerateDisabled = (0, react_1.useMemo)(() => {
        const validTextInput = sourcesView === 'text' && textInput.trim().length >= MIN_CHARACTERS_TEXT_LIMIT;
        const validUrlInput = sourcesView === 'url' && (0, utils_1.checkValidUrl)(urlInput);
        return isProcessing || (!sources.length && !validTextInput && !validUrlInput);
    }, [isProcessing, sources, urlInput, textInput, sourcesView]);
    const document = (0, react_1.useMemo)(() => ('route' in props ? props.route.params?.document : props.document), [props]);
    (0, react_1.useEffect)(() => {
        if ('isOpen' in props && !props.isOpen) {
            setForceClose(false);
        }
    }, [props]);
    const removeChip = (0, react_1.useCallback)(async (index) => {
        setSources((oldSource) => oldSource.filter((_, sourceIndex) => sourceIndex !== index));
    }, []);
    const handleBack = (0, react_1.useCallback)(() => {
        if (sourcesView === 'main') {
            if (navigation.canGoBack()) {
                navigation.goBack();
            }
            else {
                navigation.navigate('Home');
            }
            ;
        }
        else {
            setSourcesView('main');
        }
    }, [sourcesView, navigation]);
    const handleNavigateUrlView = (0, react_1.useCallback)(() => {
        setSourcesView('url');
    }, []);
    const handleNavigateTextView = (0, react_1.useCallback)(async () => {
        setSourcesView('text');
    }, []);
    const handleUploadFile = (0, react_1.useCallback)((files) => {
        setSources((oldSources) => (oldSources.length + files.length <= MAX_SOURCES
            ? [...oldSources, ...files.map((file) => ({ type: 'file', file }))]
            : oldSources));
    }, [setSources]);
    const handleUrlInputKeydown = (0, react_1.useCallback)((event) => {
        if (event.nativeEvent.key === 'Enter') {
            const content = urlInput.trim();
            if (content.length > 0) {
                if (!(0, utils_1.checkValidUrl)(content)) {
                    return;
                }
                setSources((oldSources) => (oldSources.length < MAX_SOURCES
                    ? [...oldSources, { type: (0, utils_1.checkYoutubeUrl)(content) ? 'youtube' : 'web', content }]
                    : oldSources));
                setUrlInput('');
            }
        }
    }, [urlInput]);
    const handleTextKeydown = (0, react_1.useCallback)((event) => {
        if (event.nativeEvent.key === 'Enter') {
            const content = textInput.trim().replace(utils_1.BREAKLINE_REGEX, ' ');
            if (content.length < MIN_CHARACTERS_TEXT_LIMIT) {
                showNotification('Info', 'Text content must be at least 80 characters.', 'info');
            }
            else if (content.length > MAX_CHARACTERS_TEXT_LIMIT) {
                showNotification('Info', `Only maximum ${MAX_CHARACTERS_TEXT_LIMIT} characters are allowed`, 'info');
            }
            else {
                setSources((oldSources) => (oldSources.length < MAX_SOURCES
                    ? [...oldSources, { type: 'text', content }]
                    : oldSources));
            }
        }
    }, [textInput]);
    const handleGenerateSummary = (0, react_1.useCallback)(() => {
        let documentSource = {};
        let hasValidSource = false;
        // Handle text input when in text view
        if (sourcesView === 'text') {
            const trimmedText = textInput.trim();
            if (trimmedText.length >= MIN_CHARACTERS_TEXT_LIMIT && trimmedText.length <= MAX_CHARACTERS_TEXT_LIMIT) {
                documentSource.texts = [trimmedText.replace(utils_1.BREAKLINE_REGEX, ' ')];
                hasValidSource = true;
            }
        }
        // Handle URL input when in URL view
        if (sourcesView === 'url' && (0, utils_1.checkValidUrl)(urlInput)) {
            const urlType = (0, utils_1.checkYoutubeUrl)(urlInput) ? 'youtubeUrls' : 'webUrls';
            documentSource[urlType] = [urlInput];
            hasValidSource = true;
        }
        // Then add any other sources
        documentSource = sources.reduce((acc, source) => ({
            ...acc,
            ...(source.type === 'file'
                && source.file
                && { files: [...acc.files ?? [], source.file] }),
            ...(source.type === 'youtube'
                && source.content
                && { youtubeUrls: [...acc.youtubeUrls ?? [], source.content] }),
            ...(source.type === 'web'
                && source.content
                && { webUrls: [...acc.webUrls ?? [], source.content] }),
            ...(source.type === 'text'
                && source.content
                && { texts: [...acc.texts ?? [], source.content] }),
        }), documentSource);
        documentSource.areaOfFocus = focusOn;
        if (document) {
            dispatch((0, document_1.addDocumentSourcesLoading)(document.character_id));
            setIsProcessing(true);
            attachDocument(documentSource, document.conversationId).then((uploadedSources) => {
                if (uploadedSources) {
                    dispatch((0, document_1.deleteDocumentSourcesLoading)(document.character_id));
                    setIsProcessing(false);
                    dispatch((0, document_1.selectFiles)(uploadedSources));
                }
                ;
            });
        }
        else {
            dispatch((0, document_1.setDocumentUploadComplete)(false));
            dispatch((0, user_1.clearDocument)());
            generateDocument(documentSource, selectedLanguage, generationAbortController);
            navigation.navigate('Summary');
        }
        setSources([]);
        setForceClose(true);
        if ('onClose' in props) {
            props.onClose?.();
        }
        ;
        if ('route' in props && document) {
            props.navigation.goBack();
        }
        ;
    }, [
        sources,
        focusOn,
        selectedLanguage,
        document,
        textInput,
        sourcesView,
        generateDocument,
        attachDocument,
        dispatch,
        props,
        urlInput,
    ]);
    const Container = (0, react_1.useCallback)(({ style, children, ...restProps }) => ('route' in props ? ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: [styles_1.styles.screenContainer, style], ...restProps, children: children })) : ((0, jsx_runtime_1.jsx)(container_1.AppModal, { isOpen: !forceClose && (!('isOpen' in props) || props.isOpen), onClose: props.onClose, style: [styles_1.styles.modalContent, isMobileScreen && styles_1.styles.mobileModalContainer, style], ...restProps, children: children }))), [forceClose, isMobileScreen, props]);
    return ((0, jsx_runtime_1.jsxs)(Container, { children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.header, children: [(sourcesView !== 'main' || 'route' in props) && ((0, jsx_runtime_1.jsx)(react_native_1.Pressable, { onPress: handleBack, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faArrowLeft, color: colors_1.Colors.white, size: 16 }) })), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.headerTextContainer, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.headerText, children: document ? 'Add to Sources' : 'New Summary' }), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.subtitle, children: "Choose sources for your knowledge base." })] })] }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles_1.styles.rootContainer, children: (0, jsx_runtime_1.jsx)(ScrollCustomIndicator_1.default, { customIndicator: false, style: [
                        styles_1.styles.scrollContainer,
                        { marginBottom: isMobileScreen ? 36 : 64 }
                    ], contentContainerStyle: styles_1.styles.scrollContent, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.contentContainer, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.mainArea, children: [sourcesView === 'main' && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.mainContent, children: [(0, jsx_runtime_1.jsx)(UploadArea_1.default, { multiple: true, fileAction: handleUploadFile, fileType: ["application/pdf", "text/plain", "text/csv"], invalidTypeMessage: "Invalid file type. Please upload a PDF.", descriptionText: 'Supported file types : PDF, TXT, CSV' }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.buttonsArea, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.sectionArea, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.sectionLabel, children: "Link" }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.buttonsContainer, children: [(0, jsx_runtime_1.jsx)(SourceButton_1.default, { icon: pro_solid_svg_icons_1.faLinkSimple, text: 'Website', onPress: handleNavigateUrlView }), (0, jsx_runtime_1.jsx)(SourceButton_1.default, { icon: pro_solid_svg_icons_1.faVideo, text: 'Youtube', onPress: handleNavigateUrlView })] })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.sectionArea, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.sectionLabel, children: "Text" }), (0, jsx_runtime_1.jsx)(SourceButton_1.default, { icon: pro_solid_svg_icons_1.faPaste, text: 'Paste Text', onPress: handleNavigateTextView })] })] })] })), sourcesView === 'url' && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.inputArea, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.sectionArea, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.sectionLabel, children: "URL (Hit Enter to add more URLs)" }), (0, jsx_runtime_1.jsx)(react_native_1.TextInput, { value: urlInput, onChangeText: setUrlInput, onKeyPress: (handleUrlInputKeydown), placeholder: 'Enter PDF or YouTube URL', placeholderTextColor: 'rgba(255, 255, 255, 0.40)', style: [styles_1.styles.focusInput, { outline: 'none' }] })] }), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.inputAnnotation, children: (0, utils_1.checkValidUrl)(urlInput)
                                                    ? 'Press Enter to add this URL as a source'
                                                    : '* Make sure you enter a valid Web or YouTube URL' })] })), sourcesView === 'text' && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.sectionArea, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.sectionLabel, children: "Text" }), (0, jsx_runtime_1.jsx)(react_native_1.TextInput, { value: textInput, multiline: true, onChangeText: setTextInput, onKeyPress: (handleTextKeydown), placeholder: 'Add Content (Minimum 80 characters, press Enter to finalize)', placeholderTextColor: 'rgba(255, 255, 255, 0.40)', style: [styles_1.styles.input, { outline: 'none' }] })] }))] }), (sources.length || document) && ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.sectionArea, children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.sectionHeader, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.sectionLabel, children: "Sources" }), (0, jsx_runtime_1.jsxs)(AppText_1.AppText, { style: styles_1.styles.sectionLabel, children: [(document?.files?.length) || 0 + sources.length, "/", MAX_SOURCES] })] }), (0, jsx_runtime_1.jsxs)(ScrollCustomIndicator_1.default, { ref: sourcesRef, customIndicator: true, containerStyle: styles_1.styles.chipsContainer, contentContainerStyle: styles_1.styles.chipsScrollContainer, onContentSizeChange: () => {
                                            sourcesRef.current?.scrollToEnd();
                                        }, children: [document && (document.files ? document.files.map((file, index) => ((0, jsx_runtime_1.jsx)(SourceChip_1.default, { source: file.sourceUrl || file.urlPath, displayName: file.sourceUrl || file.fileName }, index))) : ((0, jsx_runtime_1.jsx)(SourceChip_1.default, { source: document?.fileUrl ?? '', displayName: document?.fileUrl }))), sources.map((source, index) => ((0, jsx_runtime_1.jsx)(SourceChip_1.default, { source: source.type === 'file' && source.file
                                                    || (source.type === 'youtube' || source.type === 'web') && source.content
                                                    || source.type === 'text' && source.content
                                                    || '', freeText: source.type === 'text', onClose: () => removeChip(index) }, document?.files?.length ?? 0 + index)))] })] })), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.sectionArea, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles_1.styles.sectionLabel, children: "Focus on" }), (0, jsx_runtime_1.jsx)(react_native_1.TextInput, { value: focusOn, onChangeText: setFocusOn, placeholder: 'The entire document...', placeholderTextColor: 'rgba(255, 255, 255, 0.40)', style: [styles_1.styles.focusInput, { outline: 'none' }] })] })] }) }) }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles_1.styles.stickyBottom, children: [(0, jsx_runtime_1.jsx)(LanguageSelectButton_1.default, { value: selectedLanguage, setValue: setSelectedLanguage, style: styles_1.styles.languageButton }), (0, jsx_runtime_1.jsx)(GradientButton_1.GradientButton, { text: isProcessing
                            ? "Processing..."
                            : document
                                ? "Add Sources"
                                : "Generate Summary", loading: isProcessing, loadingText: 'Processing...', disabled: isGenerateDisabled, fullWidth: false, onPress: handleGenerateSummary, style: [styles_1.styles.submitButton, isMobileScreen && styles_1.styles.submitButtonMobile] })] })] }));
};
exports.default = AddSources;
